import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { SocialService } from '../application/social.service';
import { AuthenticationService } from '../application/authentication.service';
import { UserAccessService } from '@cnp/services';

/**
 * Guards the public landing page (`/`).
 *
 * The home page is only meant for anonymous visitors (and crawlers). An already
 * authenticated user hitting the root should keep the previous behaviour: land on
 * their home — the global feed, or their workspace hub for hub-sandboxed
 * WORKSPACE users — instead of seeing the marketing page.
 *
 * Auth is resolved by an APP_INITIALIZER, but with
 * `withEnabledBlockingInitialNavigation()` that initializer and the router's
 * initial navigation race: `isLogged` may still be false when the guard first
 * runs. So, exactly like {@link AuthenticationGuard}, we explicitly await
 * `verifyIfIsLoggedIn()` before deciding instead of trusting the cached flag.
 *
 * On the server the session is never available, so SSR always renders the public
 * page and the redirect happens client-side after hydration.
 */
@Injectable({
  providedIn: 'root',
})
export class HomeRedirectGuard {
  private _socialService = inject(SocialService);
  private _router = inject(Router);
  private _authService = inject(AuthenticationService);
  private _userAccessService = inject(UserAccessService);

  private readonly _isBrowser: boolean;

  constructor() {
    const platformId = inject(PLATFORM_ID);

    this._isBrowser = isPlatformBrowser(platformId);
  }

  public async canActivate(): Promise<boolean | UrlTree> {
    // On the server the session isn't available — render the public page (SEO).
    if (!this._isBrowser) {
      return true;
    }

    // Make sure auth is actually resolved before deciding (see class doc).
    if (!this._authService.isLogged) {
      await this._authService.verifyIfIsLoggedIn(null, false);
    }

    // Anonymous visitors get the public landing page.
    if (!this._authService.isLogged) {
      return true;
    }

    try {
      const userInstance = await firstValueFrom(
        this._socialService.getUserInstance()
      );
      return this._router.createUrlTree(
        this._userAccessService.resolveHome(userInstance)
      );
    } catch {
      // If the instance can't be resolved, fall back to rendering the home page
      // rather than risk a redirect loop.
      return true;
    }
  }
}
